import type { ComponentType, CSSProperties } from "react";
import { useTourState, useTourStoreApi } from "@latelierdanslesnuages/player";

/**
 * ============================================================================
 *  Drapeaux.tsx — Choisir la langue d'un coup d'œil
 * ----------------------------------------------------------------------------
 *  Remplace le sélecteur du moteur, qui affiche « FR » et « EN » dans un encart
 *  légendé. Deux drapeaux disent la même chose en moins de place, et surtout
 *  sans demander à lire — ce qui est bienvenu quand on ne lit justement pas
 *  encore la langue affichée.
 *
 *  ── Pourquoi ici et non dans le moteur ─────────────────────────────────────
 *
 *  Un drapeau n'est PAS une langue : l'anglais n'appartient pas au Royaume-Uni,
 *  et une visite qui s'ouvrirait à l'espagnol ou à l'arabe rendrait le raccourci
 *  franchement faux. Le moteur ne peut donc pas associer un pays à un code de
 *  langue ; ce projet-ci le peut, parce qu'il sait qu'il ne parlera que
 *  français et anglais.
 *
 *  ── Les dessins sont en ligne, pas en fichiers ─────────────────────────────
 *
 *  Deux drapeaux valent une centaine d'octets chacun : les sortir en `.svg`
 *  ajouterait deux requêtes et un état de chargement pour rien.
 * ==========================================================================*/

/**
 * Boîte commune, en rapport 3:2. C'est le rapport du drapeau français ; celui
 * du Royaume-Uni est en 2:1 et se trouve donc légèrement étiré en hauteur.
 * C'est le compromis habituel des jeux d'icônes : deux drapeaux alignés doivent
 * avoir la même silhouette, sans quoi l'un paraît plus important que l'autre.
 */
const RATIO = "0 0 60 40";

function DrapeauFrance() {
  return (
    <svg viewBox={RATIO} width="100%" height="100%" aria-hidden focusable="false">
      <rect width="20" height="40" fill="#002395" />
      <rect x="20" width="20" height="40" fill="#ffffff" />
      <rect x="40" width="20" height="40" fill="#ED2939" />
    </svg>
  );
}

function DrapeauRoyaumeUni() {
  return (
    <svg viewBox={RATIO} width="100%" height="100%" aria-hidden focusable="false">
      {/* Dessiné dans sa géométrie officielle (60×30), puis étiré à la boîte :
          les proportions internes des croix restent justes entre elles. */}
      <g transform="scale(1 1.3333)">
        <clipPath id="drapeau-uk-diagonales">
          {/* Ne garde qu'un demi-quadrant de chaque diagonale : c'est ce qui
              donne au sautoir rouge son décalage caractéristique. */}
          <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
        </clipPath>
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#ffffff" strokeWidth="6" />
        <path
          d="M0,0 L60,30 M60,0 L0,30"
          clipPath="url(#drapeau-uk-diagonales)"
          stroke="#C8102E"
          strokeWidth="4"
        />
        <path d="M30,0 v30 M0,15 h60" stroke="#ffffff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
      </g>
    </svg>
  );
}

/**
 * Ce que ce projet sait de ses deux langues. Une langue absente de cette table
 * n'est pas affichée : mieux vaut un choix incomplet qu'un drapeau arbitraire.
 */
const DRAPEAUX: Record<string, { titre: string; dessin: ComponentType }> = {
  fr: { titre: "Français", dessin: DrapeauFrance },
  en: { titre: "English", dessin: DrapeauRoyaumeUni },
};

const bouton: CSSProperties = {
  width: 30,
  height: 20,
  padding: 0,
  border: "1px solid rgba(255,255,255,0.35)",
  borderRadius: 3,
  // Le SVG doit être rogné par le rayon du bouton, sinon ses angles dépassent.
  overflow: "hidden",
  background: "none",
  cursor: "pointer",
  lineHeight: 0,
  pointerEvents: "auto",
  // La langue courante est en pleine couleur, l'autre en retrait. Un contour
  // seul ne suffirait pas : sur une photographie, il se perd.
  transition: "opacity 160ms ease, border-color 160ms ease",
};

export interface DrapeauxProps {
  style?: CSSProperties;
}

export function Drapeaux({ style }: DrapeauxProps) {
  const store = useTourStoreApi();
  const langues = useTourState((state) => state.tour.languages);
  const courante = useTourState((state) => state.context.language);

  const disponibles = langues.filter((code) => code in DRAPEAUX);
  if (disponibles.length < 2) return null;

  return (
    <div style={{ display: "flex", gap: 6, ...style }}>
      {disponibles.map((code) => {
        const { titre, dessin: Dessin } = DRAPEAUX[code]!;
        const active = code === courante;
        return (
          <button
            key={code}
            type="button"
            lang={code}
            title={titre}
            aria-label={titre}
            aria-pressed={active}
            onClick={() => store.getState().setLanguage(code)}
            style={{
              ...bouton,
              opacity: active ? 1 : 0.45,
              borderColor: active ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.3)",
            }}
          >
            <Dessin />
          </button>
        );
      })}
    </div>
  );
}
